"use client";

import { createContext, use, type ReactNode } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { z } from 'zod';

const FORM_MODE_FIELD = "__form_mode";
const EDITABLE_FIELDS_FIELD = "__editable_fields";
const EDITABLE_SECTIONS_FIELD = "__editable_sections";

type FormRenderMode = "active" | "inherited" | "historic";
export type InheritedFieldMode = "active" | "inherited";

interface InheritedFormModeContextValue {
  mode: FormRenderMode;
  editableFields: ReadonlySet<string>;
  editableSections: ReadonlySet<string>;
}

const InheritedFormModeContext = createContext<InheritedFormModeContextValue>({
  mode: "active",
  editableFields: new Set<string>(),
  editableSections: new Set<string>(),
});

const EMPTY_EDITABLE_TOKENS: readonly string[] = [];
const tokenInputSchema = z.union([
  z.string(),
  z.array(z.union([z.string(), z.number(), z.boolean(), z.null()])),
]);
type TokenInput = z.infer<typeof tokenInputSchema>;

interface InheritedFormModeProviderProps {
  children: ReactNode;
  defaultMode?: FormRenderMode;
  fixedMode?: FormRenderMode;
  editableFields?: readonly string[];
  editableSections?: readonly string[];
}

const normalizeTokens = (value: TokenInput): string[] => {
  if (Array.isArray(value)) {
    return value.flatMap((item) => {
      const token = String(item).trim();
      return token ? [token] : [];
    });
  }

  const normalized = value.trim();
  if (!normalized) {
    return [];
  }

  try {
    const parsed = tokenInputSchema.safeParse(JSON.parse(normalized));
    if (parsed.success && Array.isArray(parsed.data)) {
      return parsed.data.flatMap((item) => {
        const token = String(item).trim();
        return token ? [token] : [];
      });
    }
  } catch {
  }

  return normalized
    .split(/[,;\n]/)
    .map((token) => token.trim())
    .filter(Boolean);
};

const matchesEditableEntry = (fieldName: string, entry: string): boolean => {
  return (
    fieldName === entry ||
    fieldName.startsWith(`${entry}.`) ||
    fieldName.startsWith(`${entry}[`)
  );
};

const normalizeProvidedTokens = (tokens: readonly string[]): string[] =>
  tokens.flatMap((token) => {
    const normalized = token.trim();
    return normalized ? [normalized] : [];
  });

export function InheritedFormModeProvider({
  children,
  defaultMode = "active",
  fixedMode,
  editableFields = EMPTY_EDITABLE_TOKENS,
  editableSections = EMPTY_EDITABLE_TOKENS,
}: InheritedFormModeProviderProps) {
  const { control } = useFormContext();
  const rawMode = useWatch({ control, name: FORM_MODE_FIELD });
  const rawEditableFields = useWatch({ control, name: EDITABLE_FIELDS_FIELD });
  const rawEditableSections = useWatch({ control, name: EDITABLE_SECTIONS_FIELD });
  const parsedEditableFields = tokenInputSchema.safeParse(rawEditableFields);
  const parsedEditableSections = tokenInputSchema.safeParse(rawEditableSections);

  const normalizedMode = fixedMode ?? (
    rawMode === "inherited" || rawMode === "historic" || rawMode === "active"
      ? rawMode
      : defaultMode
  );
  const value: InheritedFormModeContextValue = {
    mode: normalizedMode,
    editableFields: new Set([
      ...normalizeTokens(parsedEditableFields.success ? parsedEditableFields.data : ''),
      ...normalizeProvidedTokens(editableFields),
    ]),
    editableSections: new Set([
      ...normalizeTokens(parsedEditableSections.success ? parsedEditableSections.data : ''),
      ...normalizeProvidedTokens(editableSections),
    ]),
  };

  return (
    <InheritedFormModeContext.Provider value={value}>
      {children}
    </InheritedFormModeContext.Provider>
  );
}

export function useInheritedFormMode() {
  const context = use(InheritedFormModeContext);

  const isFieldEditable = (fieldName: string, sectionId?: string): boolean => {
    if (context.mode === "active") {
      return true;
    }

    if (context.mode === "historic") {
      return false;
    }

    if (sectionId && context.editableSections.has(sectionId)) {
      return true;
    }

    for (const entry of context.editableFields) {
      if (matchesEditableEntry(fieldName, entry)) {
        return true;
      }
    }

    return false;
  };

  const hasAnyEditableField = (fieldNames: string[], sectionId?: string): boolean => {
    return fieldNames.some((fieldName) => isFieldEditable(fieldName, sectionId));
  };

  const isSectionReadOnly = (sectionId?: string, fieldNames: string[] = []): boolean => {
    if (context.mode === "active") {
      return false;
    }

    if (context.mode === "historic") {
      return true;
    }

    if (sectionId && context.editableSections.has(sectionId)) {
      return false;
    }

    if (fieldNames.length === 0) {
      return true;
    }

    return !hasAnyEditableField(fieldNames, sectionId);
  };

  const resolveFieldMode = (fieldName: string, sectionId?: string): InheritedFieldMode =>
    isFieldEditable(fieldName, sectionId) ? "active" : "inherited";

  const resolveFieldDisabled = (
    fieldName: string,
    baseDisabled = false,
    sectionId?: string,
  ): boolean => {
    return baseDisabled || resolveFieldMode(fieldName, sectionId) === "inherited";
  };

  return {
    mode: context.mode,
    editableFields: context.editableFields,
    editableSections: context.editableSections,
    isInherited: context.mode !== "active",
    isHistoric: context.mode === "historic",
    sideEffectsAllowed: context.mode === "active",
    isFieldEditable,
    hasAnyEditableField,
    isSectionReadOnly,
    resolveFieldMode,
    resolveFieldDisabled,
  };
}
